import { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import Navbar from "./Navbar";
import { BackButton } from "./BackButton";
import Chatbot from "./Chatbot";
import { MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import useAuthStore from "../stores/authStore";
import { useInactivityTimeout } from "../hooks/useInactivityTimeout";
import PendingJoinRequestsNotifier from "./PendingJoinRequestsNotifier";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { getSocket } from "../lib/socket";

export default function Layout() {
    useInactivityTimeout(); // Session timeout — auto-logout after 30 min inactivity
    const [isChatbotOpen, setIsChatbotOpen] = useState(false);
    const user = useAuthStore((state) => state.user);
    const isLearner = user?.role === "learner";
    const queryClient = useQueryClient();

    // Single global subscriber for the realtime `notification` event. Fires
    // a toast, invalidates the activity-page query, and dispatches a window
    // event so surfaces outside React Query (Navbar's unread badge) can
    // refresh on the next tick. Mounted in Layout so every authenticated
    // route benefits — the bell badge ticks even when the user isn't on
    // /notifications.
    useEffect(() => {
        const s = getSocket();
        if (!s) return;

        const onNotification = (payload) => {
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
            // The Navbar polls `/notifications/unread-count` with local state,
            // not TanStack. Surface a window event so it can refresh without
            // either side knowing about the other.
            window.dispatchEvent(new CustomEvent("vr:notification", { detail: payload }));

            const isLive = Boolean(payload?.liveTime);
            const message = payload?.message
                || (payload?.courseName ? `${payload.courseName}: update` : "New notification");
            toast(message, { icon: isLive ? "🔴" : "🔔" });
        };

        s.on("notification", onNotification);
        return () => s.off("notification", onNotification);
    }, [queryClient]);

    return (
        <div className="flex flex-col h-screen w-full overflow-hidden bg-background text-foreground transition-colors duration-300">
            {/* Top Navbar */}
            <Navbar />

            {/* Main Content Area */}
            <main className="flex-1 overflow-y-auto scroll-smooth relative pt-[56px] md:pt-0">
                {/* Removed hardcoded background overlays to allow index.css --background to show */}

                <div className="relative z-10 p-6 lg:p-8 max-w-7xl mx-auto w-full">
                    {/* Back button for non-root pages */}
                    <BackButton />

                    <Outlet />
                </div>
            </main>

            {/* Live-class join request notifier — self-mutes for learners */}
            <PendingJoinRequestsNotifier />

            {/* Chatbot Integration - Only for Learners */}
            {isLearner && (
                <>
                    <Chatbot isOpen={isChatbotOpen} onClose={() => setIsChatbotOpen(false)} />

                    {!isChatbotOpen && (
                        <Button
                            onClick={() => setIsChatbotOpen(true)}
                            className="fixed bottom-6 right-6 z-50 bg-primary hover:bg-primary/90 text-primary-foreground w-14 h-14 hover:w-auto hover:px-6 rounded-full shadow-lg transition-all duration-300 ease-in-out hover:scale-105 active:scale-95 flex items-center justify-center gap-0 hover:gap-3 group overflow-hidden"
                        >
                            <MessageSquare className="h-6 w-6 shrink-0" />
                            <span className="font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 max-w-0 group-hover:max-w-xs transition-all duration-300 ease-in-out">
                                Need Help?
                            </span>
                        </Button>
                    )}
                </>
            )}
        </div>
    );
}
